import { Autocomplete, Button, TextField, Box, Typography } from '@mui/material'
import { Platform, useMediaAccountsQuery } from 'graphql/generated/graphql'
import { IMediaAccounts } from '../../../components/PostScheduling/PostScheduling'
import React from 'react'
import { Instagram, Sparkles, TrendingUp } from 'lucide-react'

interface AnalyzeFollowersInitialProps {
  analyzeFollowers: any
  refetch: any
}

export default function AnalyzeFollowersInitial({
  analyzeFollowers,
  refetch,
}: AnalyzeFollowersInitialProps): JSX.Element {
  const [profileValue, setProfileValue] = React.useState<IMediaAccounts | undefined>()
  const [isLoading, setIsLoading] = React.useState(false)

  const { data, loading } = useMediaAccountsQuery({
    variables: {
      platform: Platform.Instagram,
    },
  })

  const handleProceed = () => {
    if (profileValue?.id) {
      setIsLoading(true)
      analyzeFollowers({
        variables: {
          accountId: profileValue.id,
        },
      })
        .then(() => {
          refetch()
        })
        .catch(() => {
          refetch()
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
      }}
    >
      <Box
        sx={{
          maxWidth: '800px',
          width: '100%',
          background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(88, 28, 135, 0.4) 100%)',
          backdropFilter: 'blur(20px)',
          borderRadius: '24px',
          border: '1px solid rgba(168, 85, 247, 0.2)',
          padding: '48px',
          boxShadow: '0 20px 60px rgba(168, 85, 247, 0.2)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, #a855f7 0%, #ec4899 100%)',
          },
        }}
      >
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '80px',
              height: '80px',
              borderRadius: '20px',
              background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
              mb: 3,
              boxShadow: '0 8px 32px rgba(168, 85, 247, 0.4)',
            }}
          >
            <Sparkles size={40} color="white" />
          </Box>

          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(135deg, #ffffff 0%, #c084fc 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 2,
              fontSize: { xs: '28px', md: '36px' },
            }}
          >
            Unlock Follower Insights
          </Typography>

          <Typography
            sx={{
              fontSize: '16px',
              color: 'rgba(255, 255, 255, 0.7)',
              lineHeight: 1.6,
              maxWidth: '600px',
              margin: '0 auto',
            }}
          >
            Get deep analytics on your most engaged Instagram followers. Discover who they are, what
            they care about, and how to connect with them.
          </Typography>
        </Box>

        {/* Features Grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: '16px',
            mb: 4,
          }}
        >
          {[
            { icon: TrendingUp, label: 'Engagement Analytics', color: '#a855f7' },
            { icon: Instagram, label: 'Top Followers', color: '#ec4899' },
            { icon: Sparkles, label: 'AI Insights', color: '#8b5cf6' },
          ].map((feature, idx) => (
            <Box
              key={idx}
              sx={{
                background: 'rgba(30, 41, 59, 0.5)',
                borderRadius: '12px',
                padding: '16px',
                border: '1px solid rgba(168, 85, 247, 0.2)',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'rgba(168, 85, 247, 0.1)',
                  border: '1px solid rgba(168, 85, 247, 0.4)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <Box
                sx={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: `${feature.color}20`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <feature.icon size={20} style={{ color: feature.color }} />
              </Box>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'rgba(255, 255, 255, 0.9)',
                }}
              >
                {feature.label}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Selection Section */}
        <Box
          sx={{
            background: 'rgba(15, 23, 42, 0.5)',
            borderRadius: '16px',
            padding: '24px',
            border: '1px solid rgba(168, 85, 247, 0.2)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mb: 3 }}>
            <Box
              sx={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Instagram size={20} color="white" />
            </Box>
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: 600,
                color: 'rgba(255, 255, 255, 0.9)',
              }}
            >
              Select Your Instagram Account
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: '12px', flexDirection: { xs: 'column', sm: 'row' } }}>
            <Autocomplete
              id="instagram-account-select"
              loading={loading}
              options={data?.mediaAccounts ?? []}
              getOptionLabel={(option) => option.accountName}
              filterSelectedOptions
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(30, 41, 59, 0.6)',
                  color: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: '12px',
                  '& fieldset': {
                    borderColor: 'rgba(168, 85, 247, 0.3)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(168, 85, 247, 0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#a855f7',
                    borderWidth: '2px',
                  },
                },
                '& .MuiAutocomplete-endAdornment': {
                  '& .MuiSvgIcon-root': {
                    color: 'rgba(255, 255, 255, 0.6)',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.6)',
                  '&.Mui-focused': {
                    color: '#c084fc',
                  },
                },
                '& .MuiAutocomplete-popupIndicator': {
                  color: 'rgba(255, 255, 255, 0.6)',
                },
                '& .MuiAutocomplete-clearIndicator': {
                  color: 'rgba(255, 255, 255, 0.6)',
                },
                '& .MuiAutocomplete-paper': {
                  background: 'rgba(30, 41, 59, 0.95)',
                  backdropFilter: 'blur(10px)',
                },
              }}
              onChange={(e, value) => {
                if (value) {
                  setProfileValue(value)
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Choose your profile"
                  sx={{
                    '& .MuiInputLabel-root': {
                      color: 'rgba(255, 255, 255, 0.6)',
                      '&.Mui-focused': {
                        color: '#c084fc',
                      },
                    },
                  }}
                />
              )}
            />

            <Button
              onClick={handleProceed}
              disabled={profileValue === undefined || isLoading}
              sx={{
                background: profileValue
                  ? 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)'
                  : 'rgba(168, 85, 247, 0.3)',
                color: '#fff',
                padding: '14px 32px',
                borderRadius: '12px',
                textTransform: 'none',
                fontSize: '15px',
                fontWeight: 600,
                minWidth: { xs: '100%', sm: '140px' },
                height: '56px',
                boxShadow: profileValue ? '0 4px 20px rgba(168, 85, 247, 0.4)' : 'none',
                '&:hover': {
                  background: profileValue
                    ? 'linear-gradient(135deg, #9333ea 0%, #db2777 100%)'
                    : 'rgba(168, 85, 247, 0.3)',
                  boxShadow: profileValue ? '0 6px 24px rgba(168, 85, 247, 0.5)' : 'none',
                  transform: profileValue ? 'translateY(-2px)' : 'none',
                },
                '&.Mui-disabled': {
                  backgroundColor: 'rgba(168, 85, 247, 0.2)',
                  color: 'rgba(255, 255, 255, 0.4)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              {isLoading ? 'Analyzing...' : 'Start Analysis'}
            </Button>
          </Box>

          {data?.mediaAccounts.length === 0 && !loading && (
            <Box
              sx={{
                mt: 2,
                padding: '12px 16px',
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <Box
                sx={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  background: '#ef4444',
                }}
              />
              <Typography sx={{ color: '#fca5a5', fontSize: '14px', fontWeight: 500 }}>
                Please connect your Instagram business account to continue
              </Typography>
            </Box>
          )}
        </Box>

        {/* Info Footer */}
        <Box
          sx={{
            mt: 4,
            pt: 3,
            borderTop: '1px solid rgba(168, 85, 247, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
          }}
        >
          <Box
            sx={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
              animation: 'pulse 2s ease-in-out infinite',
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.5 },
              },
            }}
          />
          <Typography
            sx={{
              fontSize: '13px',
              color: 'rgba(255, 255, 255, 0.5)',
              fontStyle: 'italic',
            }}
          >
            Analysis typically takes 2-3 minutes to complete
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}